import { useCallback, useEffect, useRef, useState } from 'react'
import type { RecordingDraft, TrackPoint } from '../types'
import { genId, haversineMeters, pathDistanceMeters } from '../utils/trailGeo'
import {
  saveRecordingDraft,
  clearRecordingDraft,
} from '../storage/trailStorage'

export type RecorderStatus = 'idle' | 'recording' | 'paused' | 'error'

export interface LivePosition {
  lat: number
  lng: number
  accuracy?: number
  /** Elevation in meters, when the device reports it. */
  elevation?: number
  /** Ground speed in meters/second, from the device or derived between fixes. */
  speed?: number
}

interface UseTrailRecorderResult {
  status: RecorderStatus
  /**
   * Stable id for the active recording session. Generated on start() (and on
   * restore()) so waypoint photos can be persisted against it before the trail
   * is saved, and the crash-save draft can reference it. Survives in-app
   * navigation because the hook lives above the router.
   */
  trailId: string
  /** Logged track so far (jitter-filtered). */
  points: TrackPoint[]
  /** Latest raw fix, used to show the user's current position even before it's logged. */
  current: LivePosition | null
  error: string | null
  distanceMeters: number
  elapsedMs: number
  start: () => void
  pause: () => void
  resume: () => void
  /** Stop recording and return the final logged points. */
  /** Ends the recording, returning the track and the pause-excluded moving time. */
  stop: () => { points: TrackPoint[]; movingMs: number }
  /** Clear all state back to idle (e.g. after saving). */
  reset: () => void
  /**
   * Rehydrate from a persisted crash-save draft and resume watching. Restores
   * the track/distance/elapsed and continues recording from where it left off.
   */
  restore: (draft: RecordingDraft) => void
}

// How often to checkpoint the in-progress track to IndexedDB while recording.
// Frequent enough that a crash loses at most a few seconds of track, cheap
// enough that it doesn't thrash the disk.
const CHECKPOINT_INTERVAL_MS = 10000

// Once a track is underway, discard only egregiously inaccurate fixes (meters)
// so a single wild reading doesn't spike the line. The first point is always
// logged regardless, so recording starts even when the initial fix is coarse.
const MAX_ACCURACY_M = 100
// Ignore fixes closer than this to the last logged point — GPS jitters while
// standing still and we don't want to accumulate thousands of duplicate points.
const MIN_DISTANCE_M = 4

/**
 * Continuous GPS track recorder built directly on navigator.geolocation.
 * Unlike the app's useGeolocation hook (which suppresses small movements for a
 * "current location" marker), this appends every meaningful fix to build a
 * GPX-style track. Tracking pauses/resumes cleanly and tallies live distance
 * and elapsed time.
 */
export function useTrailRecorder(): UseTrailRecorderResult {
  const [status, setStatus] = useState<RecorderStatus>('idle')
  const [trailId, setTrailId] = useState<string>(() => genId())
  const [points, setPoints] = useState<TrackPoint[]>([])
  const [current, setCurrent] = useState<LivePosition | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [distanceMeters, setDistanceMeters] = useState(0)
  const [elapsedMs, setElapsedMs] = useState(0)

  const watchIdRef = useRef<number | null>(null)
  // Mirror of `points` so stop() can return the final track synchronously
  // (React state updates aren't readable immediately after setPoints).
  const pointsRef = useRef<TrackPoint[]>([])
  const lastLoggedRef = useRef<TrackPoint | null>(null)
  // Accumulated recording time (across pause/resume) plus the timestamp of the
  // current running segment, so elapsed time excludes paused intervals.
  const accumulatedMsRef = useRef(0)
  const segmentStartRef = useRef<number | null>(null)
  const tickRef = useRef<ReturnType<typeof setInterval> | null>(null)
  // Mirrors used by the periodic checkpoint so it can snapshot the latest
  // distance/elapsed/status/trailId without re-creating the interval each time
  // those change.
  const trailIdRef = useRef<string>(trailId)
  const distanceRef = useRef(0)
  const statusRef = useRef<RecorderStatus>('idle')
  const startedAtRef = useRef<number>(Date.now())
  const checkpointRef = useRef<ReturnType<typeof setInterval> | null>(null)

  // Write the current in-progress track to IndexedDB so a crash/reload can
  // recover it. Best-effort: failures (e.g. private-mode quota) are swallowed
  // so they never interrupt recording.
  const checkpoint = useCallback(() => {
    const s = statusRef.current
    if (s !== 'recording' && s !== 'paused') return
    const segMs = segmentStartRef.current != null ? Date.now() - segmentStartRef.current : 0
    void saveRecordingDraft({
      id: 'current',
      trailId: trailIdRef.current,
      status: s,
      startedAt: startedAtRef.current,
      track: pointsRef.current,
      distanceMeters: distanceRef.current,
      elapsedMs: accumulatedMsRef.current + segMs,
      updatedAt: Date.now(),
    }).catch(() => {})
  }, [])

  const clearCheckpoint = useCallback(() => {
    if (checkpointRef.current !== null) {
      clearInterval(checkpointRef.current)
      checkpointRef.current = null
    }
  }, [])

  const clearWatch = useCallback(() => {
    if (watchIdRef.current !== null && navigator.geolocation) {
      navigator.geolocation.clearWatch(watchIdRef.current)
      watchIdRef.current = null
    }
  }, [])

  const clearTick = useCallback(() => {
    if (tickRef.current !== null) {
      clearInterval(tickRef.current)
      tickRef.current = null
    }
  }, [])

  const handlePosition = useCallback((pos: GeolocationPosition) => {
    const { latitude, longitude, accuracy, altitude, speed } = pos.coords
    // Prefer the device's reported ground speed; many devices report null, so
    // fall back to distance/time since the last logged fix.
    let liveSpeed = speed != null && Number.isFinite(speed) && speed >= 0 ? speed : undefined
    const prev = lastLoggedRef.current
    if (liveSpeed == null && prev) {
      const dt = (pos.timestamp - prev.t) / 1000
      if (dt > 0) liveSpeed = haversineMeters(prev.lat, prev.lng, latitude, longitude) / dt
    }
    setCurrent({
      lat: latitude,
      lng: longitude,
      accuracy: accuracy ?? undefined,
      elevation: altitude ?? undefined,
      speed: liveSpeed,
    })

    const last = lastLoggedRef.current
    if (last) {
      // Skip only wildly inaccurate fixes mid-track (a coarse reading would
      // jump the line). Always keep the first point so recording starts.
      if (accuracy != null && accuracy > MAX_ACCURACY_M) return
      const moved = haversineMeters(last.lat, last.lng, latitude, longitude)
      if (moved < MIN_DISTANCE_M) return
      distanceRef.current += moved
      setDistanceMeters(distanceRef.current)
    }

    const tp: TrackPoint = {
      lat: latitude,
      lng: longitude,
      accuracy: accuracy ?? undefined,
      elevation: altitude ?? undefined,
      speed: liveSpeed,
      t: pos.timestamp,
    }
    lastLoggedRef.current = tp
    pointsRef.current = [...pointsRef.current, tp]
    setPoints(pointsRef.current)
  }, [])

  const handleError = useCallback((err: GeolocationPositionError) => {
    // Permission denial is terminal; transient errors (timeout) are ignored so
    // watchPosition can recover on the next fix.
    if (err.code === err.PERMISSION_DENIED) {
      setError('Location permission denied. Enable location access to record a trail.')
      setStatus('error')
      clearWatch()
      clearTick()
    }
  }, [clearWatch, clearTick])

  const beginWatch = useCallback(() => {
    if (!navigator.geolocation) {
      setError('Geolocation is not supported on this device.')
      setStatus('error')
      return
    }
    if (!window.isSecureContext) {
      setError('Recording requires a secure (HTTPS) connection.')
      setStatus('error')
      return
    }
    clearWatch()
    watchIdRef.current = navigator.geolocation.watchPosition(handlePosition, handleError, {
      enableHighAccuracy: true,
      maximumAge: 0,
      timeout: 15000,
    })
    segmentStartRef.current = Date.now()
    clearTick()
    tickRef.current = setInterval(() => {
      const segMs = segmentStartRef.current != null ? Date.now() - segmentStartRef.current : 0
      setElapsedMs(accumulatedMsRef.current + segMs)
    }, 1000)
    // Crash-save the in-progress track on a slow cadence alongside the watch.
    clearCheckpoint()
    checkpointRef.current = setInterval(checkpoint, CHECKPOINT_INTERVAL_MS)
  }, [clearWatch, clearTick, clearCheckpoint, checkpoint, handlePosition, handleError])

  const start = useCallback(() => {
    setPoints([])
    // Keep the last known position so the map stays centered on the user
    // instead of flicking back to the default view while the first fix lands.
    setError(null)
    setDistanceMeters(0)
    setElapsedMs(0)
    pointsRef.current = []
    lastLoggedRef.current = null
    accumulatedMsRef.current = 0
    segmentStartRef.current = null
    distanceRef.current = 0
    // Fresh session id so this recording's photos/draft don't collide with a
    // previous one.
    const id = genId()
    trailIdRef.current = id
    setTrailId(id)
    startedAtRef.current = Date.now()
    statusRef.current = 'recording'
    setStatus('recording')
    beginWatch()
  }, [beginWatch])

  const pause = useCallback(() => {
    if (segmentStartRef.current != null) {
      accumulatedMsRef.current += Date.now() - segmentStartRef.current
      segmentStartRef.current = null
    }
    clearWatch()
    clearTick()
    clearCheckpoint()
    statusRef.current = 'paused'
    setStatus('paused')
    // Persist the paused state so a reload while paused recovers correctly.
    checkpoint()
  }, [clearWatch, clearTick, clearCheckpoint, checkpoint])

  const resume = useCallback(() => {
    statusRef.current = 'recording'
    setStatus('recording')
    beginWatch()
  }, [beginWatch])

  /**
   * Ends the recording and hands back the track *and* the time actually spent moving.
   *
   * `movingMs` excludes paused stretches, which the wall clock between the first and
   * last fix cannot tell you. It was being computed and displayed throughout the walk
   * and then dropped on the floor here, so a hike with a long lunch stop was saved as
   * though the walker had been going the whole time.
   */
  const stop = useCallback((): { points: TrackPoint[]; movingMs: number } => {
    if (segmentStartRef.current != null) {
      accumulatedMsRef.current += Date.now() - segmentStartRef.current
      segmentStartRef.current = null
    }
    clearWatch()
    clearTick()
    clearCheckpoint()
    statusRef.current = 'idle'
    setStatus('idle')
    // The track is in hand now; drop the crash-save draft so a later reload
    // doesn't offer to resume a finished hike.
    void clearRecordingDraft().catch(() => {})
    return { points: pointsRef.current, movingMs: accumulatedMsRef.current }
  }, [clearWatch, clearTick, clearCheckpoint])

  const reset = useCallback(() => {
    clearWatch()
    clearTick()
    clearCheckpoint()
    pointsRef.current = []
    lastLoggedRef.current = null
    accumulatedMsRef.current = 0
    segmentStartRef.current = null
    distanceRef.current = 0
    statusRef.current = 'idle'
    setPoints([])
    setCurrent(null)
    setError(null)
    setDistanceMeters(0)
    setElapsedMs(0)
    setStatus('idle')
    void clearRecordingDraft().catch(() => {})
  }, [clearWatch, clearTick, clearCheckpoint])

  // Rehydrate from a persisted draft (after a crash/reload) and continue
  // recording. Restores the logged track, distance and accumulated time, then
  // starts a fresh watch segment so new fixes append to the recovered track.
  const restore = useCallback(
    (draft: RecordingDraft) => {
      clearWatch()
      clearTick()
      clearCheckpoint()
      const track = draft.track ?? []
      pointsRef.current = track
      lastLoggedRef.current = track.length ? track[track.length - 1] : null
      distanceRef.current = draft.distanceMeters ?? 0
      accumulatedMsRef.current = draft.elapsedMs ?? 0
      segmentStartRef.current = null
      trailIdRef.current = draft.trailId
      startedAtRef.current = draft.startedAt ?? Date.now()
      statusRef.current = 'recording'
      setTrailId(draft.trailId)
      setPoints(track)
      setDistanceMeters(distanceRef.current)
      setElapsedMs(accumulatedMsRef.current)
      setError(null)
      setStatus('recording')
      beginWatch()
    },
    [beginWatch, clearWatch, clearTick, clearCheckpoint]
  )

  // Keep the checkpoint mirrors in sync with the latest status/trailId so the
  // periodic snapshot (which reads refs, not state) sees current values.
  useEffect(() => {
    statusRef.current = status
  }, [status])
  useEffect(() => {
    trailIdRef.current = trailId
  }, [trailId])

  // Grab an initial fix on mount so the map can center on the user before
  // recording starts. Favor speed over precision here: a coarse/cached fix
  // (low accuracy, recent cache) centers the map in ~1s instead of waiting on
  // a high-accuracy GPS cold start, which on mobile can exceed 10s and leave
  // the map on the fallback view. The watch refines this once recording begins.
  useEffect(() => {
    if (!navigator.geolocation || !window.isSecureContext) return
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setCurrent({
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          accuracy: pos.coords.accuracy ?? undefined,
        })
      },
      () => {
        // Ignore — the user can still start recording; watchPosition handles
        // the permission-denied path with a visible error.
      },
      { enableHighAccuracy: false, timeout: 20000, maximumAge: 300000 }
    )
  }, [])

  // Clean up watch/timer if the component unmounts mid-recording. Mounted above
  // the router by RecorderProvider, so this only fires when the whole protected
  // layout tears down (e.g. logout) — not on in-app navigation.
  useEffect(() => {
    return () => {
      clearWatch()
      clearTick()
      clearCheckpoint()
    }
  }, [clearWatch, clearTick, clearCheckpoint])

  return {
    status,
    trailId,
    points,
    current,
    error,
    distanceMeters,
    elapsedMs,
    start,
    pause,
    resume,
    stop,
    reset,
    restore,
  }
}

/** Recompute distance from a finished point set (used when persisting). */
export function trackDistance(points: TrackPoint[]): number {
  return pathDistanceMeters(points)
}
